import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, Eye, Mail } from "lucide-react";
import PublicFooter from "../components/PublicFooter";
import LastUpdatedBadge from "../components/LastUpdatedBadge";
import usePageMeta from "../hooks/usePageMeta";

const SECTIONS = [
  {
    icon: Database,
    title: "What we collect",
    body: [
      "Merchant details (business name, registration number, KRA PIN, owner identity).",
      "Customer details when relevant for cover (e.g. claimant name and contact).",
      "Payment references and provider transaction IDs.",
      "Claim evidence such as screenshots, M-Pesa statements, police reports, and incident notes.",
      "WhatsApp FlashClaim submissions including chat content and attachments.",
      "Device and session data (IP, user-agent, login time, fingerprint hashes) for fraud protection.",
      "Fraud-risk signals: behavioural metrics, image perceptual hashes, anomaly flags.",
    ],
  },
  {
    icon: Eye,
    title: "Why we collect it",
    body: [
      "Setting up your policy and verifying eligibility for cover.",
      "Reviewing and deciding claims (FlashClaim auto-approval, manual review, or SIU escalation).",
      "Preventing fraud — duplicate claims, identity mismatches, suspicious payouts.",
      "Verifying payments and reconciling premiums with your policy.",
      "Providing customer support and responding to your questions.",
      "Meeting our compliance and reporting obligations under Kenyan law and our insurance partners.",
    ],
  },
  {
    icon: Shield,
    title: "How we keep it safe",
    body: [
      "Sensitive data should only be submitted where it is needed for cover or claims — please don't share more than that.",
      "All connections to Fortiqa use TLS encryption.",
      "Admin staff access is role-restricted and audit-logged.",
      "Two-factor authentication is available for admin and finance accounts.",
      "Claim evidence files are retained only as long as compliance requires.",
    ],
  },
];

export default function Privacy() {
  usePageMeta(
    "Privacy · Fortiqa",
    "How Fortiqa collects, uses, and protects merchant and claim data, with a clear contact for privacy questions.",
  );
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#114232]" data-testid="privacy-page">
      <header className="px-6 lg:px-10 py-6 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs text-[#5c6360] hover:text-[#114232] inline-flex items-center gap-1"
              data-testid="back-to-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Fortiqa
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#c15337]">Privacy</div>
      </header>
      <main className="px-6 lg:px-10 max-w-3xl mx-auto py-4">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Your data, handled with care</h1>
        <div className="mb-4"><LastUpdatedBadge pageKey="privacy" /></div>
        <p className="text-sm sm:text-base text-[#5c6360] mb-8 max-w-2xl">
          Fortiqa protects merchants against cyber incidents — that means we
          handle sensitive data about your business, your customers, and your
          payments. Here's exactly what we collect, why we collect it, and how
          we keep it safe.
        </p>

        {SECTIONS.map((s) => (
          <section key={s.title} className="bg-white border border-[#114232]/10 rounded-xl p-5 sm:p-6 mb-4"
                    data-testid={`privacy-section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <s.icon className="w-5 h-5 text-[#c15337]" />
              <h2 className="font-heading text-lg sm:text-xl font-bold">{s.title}</h2>
            </div>
            <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-[#1b1d1c]">
              {s.body.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </section>
        ))}

        <section className="bg-[#114232]/5 border border-[#114232]/15 rounded-xl p-5 sm:p-6 mb-8"
                  data-testid="privacy-contact">
          <div className="flex items-center gap-2.5 mb-2">
            <Mail className="w-5 h-5 text-[#c15337]" />
            <h2 className="font-heading text-lg sm:text-xl font-bold">Privacy questions or requests</h2>
          </div>
          <p className="text-sm sm:text-base text-[#1b1d1c]">
            Reach our privacy team at{" "}
            <a href="mailto:privacy@fortiqa.co.ke"
                className="text-[#c15337] hover:underline font-semibold"
                data-testid="privacy-email">privacy@fortiqa.co.ke</a>
            . We respond within 5 business days.
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
